import { Service } from "../types";
import {
  V1ProjectServiceResource,
  V1ProjectSubserviceResource,
  V1Relationships,
} from "@generated";
import API from "../../../../utils/API/API";

interface Resource {
  data: { id: string; type: "project-resources" };
}

export const getResourceName = (resource) =>
  resource?.attributes?.["extended-name"] || resource?.attributes?.name || "";

export const formatResource = (service): Resource | undefined => {
  if (!service?.resource?.id || service?.resource?.id === -1) return;
  const resourceId = service?.resource?.id?.split("-").pop();
  const resourceType = service?.resource?.id?.split("-").slice(0, -1).join("-");

  return {
    data: {
      id:
        resourceType === "project-resources"
          ? resourceId
          : String(service?.resource?.id),
      type: "project-resources",
    },
  };
};

export interface ApplyFailure {
  serviceId: string;
  name: string;
  message: string;
  // True when the service itself was added and only a follow-up step failed,
  // so it must not be applied again.
  applied: boolean;
}

// The API's own explanation, so the user can see what to correct.
export const apiErrorMessage = (err: any): string => {
  const errors = err?.response?.data?.errors;
  if (Array.isArray(errors) && errors.length) {
    return errors
      .map((e) => e?.detail || e?.title)
      .filter(Boolean)
      .join("; ");
  }
  if (err?.response?.status) {
    return `The server returned an error (${err.response.status}).`;
  }
  return err?.message || "Something went wrong.";
};

// Applies each service independently and reports every failure rather than
// stopping at the first, so the caller can keep the user on the page with
// the reasons listed and drop the services that were added.
export const handleCreateProjectServices = async (
  services: Service[],
  serviceType: "professional_services" | "managed_services",
  project_id: number,
  accountSlug: string,
  authorizationCode: string
): Promise<{ appliedIds: string[]; failures: ApplyFailure[] }> => {
  const apiHost = process.env.REACT_APP_SCOPESTACK_API_HOST;
  const appliedIds: string[] = [];
  const failures: ApplyFailure[] = [];

  for (const service of services) {
    const serviceId = String(service.id);
    const name = service?.name || "";
    const data: V1ProjectServiceResource = {
      type: "project-services",
      //@ts-ignore
      attributes: {
        name,
        quantity: Number(service?.quantity || 1),
        "override-hours": Number(service?.unitHours || 0),
        "service-type": serviceType,
      },
      relationships: {
        project: { data: { id: project_id, type: "projects" } },
        "project-resource": formatResource(service),
        service: {
          data: { id: Number(service.id), type: "services" },
        },
      },
    };

    let projectServiceId;
    try {
      const serviceResponse = await API.Post(
        `${apiHost}/${accountSlug}/v1/project-services`,
        { data },
        authorizationCode
      );
      //@ts-ignore
      projectServiceId = serviceResponse?.data?.data?.id;
    } catch (err) {
      failures.push({
        serviceId,
        name,
        message: apiErrorMessage(err),
        applied: false,
      });
      continue;
    }
    appliedIds.push(serviceId);

    const changedSubservices = (service.subservices || []).filter(
      (sub) => sub.isChanged
    );
    if (!projectServiceId || !changedSubservices.length) continue;

    try {
      const projectService = await API.Get(
        `${apiHost}/${accountSlug}/v1/project-services/${projectServiceId}?include=project-subservices,project-subservices.subservice,service,service.subservices`,
        authorizationCode
      );

      ///PATCH project-subservices with changed subservice data
      for (const subservice of changedSubservices) {
        const projectSubservice = projectService?.data?.included?.find(
          (item) =>
            item?.type === "project-subservices" &&
            item?.relationships?.subservice?.data?.id === subservice.id
        );
        if (!projectSubservice) continue;

        const subData: V1ProjectSubserviceResource = {
          id: Number(projectSubservice.id),
          type: "project-subservices",
          //@ts-ignore
          attributes: {
            name: subservice?.name || "",
            quantity: Number(subservice?.quantity || 1),
          },
          relationships: {
            "project-resource": formatResource(subservice),
            "project-service": {
              data: {
                id: projectServiceId,
                type: "project-services",
              },
            },
          },
        };

        try {
          await API.Patch(
            `${apiHost}/${accountSlug}/v1/project-subservices/${projectSubservice.id}`,
            { data: subData },
            authorizationCode
          );
        } catch (err) {
          failures.push({
            serviceId,
            name,
            message: `Subservice ${subservice?.name || ""}: ${apiErrorMessage(
              err
            )}`,
            applied: true,
          });
        }
      }
    } catch (err) {
      failures.push({
        serviceId,
        name,
        message: `Subservice changes: ${apiErrorMessage(err)}`,
        applied: true,
      });
    }
  }

  return { appliedIds, failures };
};
